import { code } from "@spscommerce/utils";
import { DSReactExamples } from "@spscommerce/ds-shared";

export const SpsFieldsetExamples: DSReactExamples = {
    basic: {
        label: "Basic Fieldsets",
        description: "info about basic fieldsets",
        examples: {
            basic: {
                react: code`
                    function DemoComponent() {
                        const RADIO_OPTIONS = [
                            { value: "a", label: "Option A" },
                            { value: "b", label: "Option B" }
                        ];
                        const { formValue, formMeta, updateForm } = useSpsForm({
                            myFieldset: {
                                foo: "",
                                bar: "",
                                baz: RADIO_OPTIONS[0].value
                            }
                        });

                        return <>
                            <SpsFieldset formMeta={formMeta.fields.myFieldset} legend="Fieldset Legend" tooltip="Add a Tooltip">
                                <div className="sfg-row">
                                    <div className="sfg-col-4">
                                        <SpsLabel for={formMeta.fields.myFieldset.fields.foo}>Foo</SpsLabel>
                                        <SpsTextInput formMeta={formMeta.fields.myFieldset.fields.foo}
                                            value={formValue.myFieldset.foo}
                                        ></SpsTextInput>
                                    </div>
                                    <div className="sfg-col-4">
                                        <SpsLabel for={formMeta.fields.myFieldset.fields.bar}>Bar</SpsLabel>
                                        <SpsTextInput formMeta={formMeta.fields.myFieldset.fields.bar}
                                            value={formValue.myFieldset.bar}
                                        ></SpsTextInput>
                                    </div>
                                    <div className="sfg-col-4">
                                        <SpsLabel for={formMeta.fields.myFieldset.fields.baz}>Baz</SpsLabel>
                                        <SpsInputGroup>
                                            {RADIO_OPTIONS.map(option => (
                                                <SpsRadioButton key={option.value} name="baz"
                                                    label={option.label} value={option.value}
                                                    formMeta={formMeta.fields.myFieldset.fields.baz}
                                                    checked={formValue.myFieldset.baz === option.value}
                                                />
                                            ))}
                                        </SpsInputGroup>
                                    </div>
                                </div>
                            </SpsFieldset>
                        </>
                    }
                `
            }
        }
    },
    b_nested: {
        label: "Nested Fieldsets",
        description: "info about nested fieldsets",
        examples: {
            nested: {
                react: code`
                    function DemoComponent() {
                        const RADIO_OPTIONS = [
                            { value: "a", label: "Option A" },
                            { value: "b", label: "Option B" }
                        ];
                        const { formValue, formMeta, updateForm } = useSpsForm({
                            topFieldset: {
                                foo: "",
                                fsNested: {
                                    bar: RADIO_OPTIONS[1].value,
                                    fsNested2: {
                                        baz: ""
                                    }
                                }
                            }
                        });

                        return <>
                            <SpsFieldset formMeta={formMeta.fields.topFieldset} legend="Fieldset Legend">
                                <div className="sfg-row">
                                    <div className="sfg-col-4">
                                        <SpsLabel for={formMeta.fields.topFieldset.fields.foo}>Foo</SpsLabel>
                                        <SpsTextInput formMeta={formMeta.fields.topFieldset.fields.foo}
                                            value={formValue.topFieldset.foo}
                                            placeholder="Enter a thing"
                                        ></SpsTextInput>
                                    </div>
                                </div>
                                <SpsFieldset formMeta={formMeta.fields.topFieldset.fields.fsNested} legend="Nested Fieldset">
                                    <div className="sfg-row">
                                        <div className="sfg-col-4">
                                            <SpsLabel for={formMeta.fields.topFieldset.fields.fsNested.fields.bar}>Bar</SpsLabel>
                                            <SpsInputGroup>
                                                {RADIO_OPTIONS.map(option => (
                                                    <SpsRadioButton key={option.value} name="fsRadio"
                                                        label={option.label} value={option.value}
                                                        formMeta={formMeta.fields.topFieldset.fields.fsNested.fields.bar}
                                                        checked={formValue.topFieldset.fsNested.bar === option.value}
                                                    />
                                                ))}
                                            </SpsInputGroup>
                                        </div>
                                    </div>
                                    <SpsFieldset formMeta={formMeta.fields.topFieldset.fields.fsNested.fields.fsNested2} legend="Doubly Nested Fieldset">
                                        <div className="sfg-row">
                                            <div className="sfg-col-4">
                                                <SpsLabel for={formMeta.fields.topFieldset.fields.fsNested.fields.fsNested2.fields.baz}>Baz</SpsLabel>
                                                <SpsTextInput formMeta={formMeta.fields.topFieldset.fields.fsNested.fields.fsNested2.fields.baz}
                                                    value={formValue.topFieldset.fsNested.baz}
                                                    placeholder="Enter a thing"
                                                ></SpsTextInput>
                                            </div>
                                        </div>
                                    </SpsFieldset>
                                </SpsFieldset>
                            </SpsFieldset>
                        </>
                    }
                `
            }
        }
    },
    c_optional: {
        label: "Optional Fieldsets",
        description: "info about optional fieldsets",
        examples: {
            optional: {
                react: code`
                    function DemoComponent() {
                        interface MyFormValue {
                            optionalFieldset?: {
                                foo: string;
                                bar: string;
                            };
                        }
                        const initValue: MyFormValue = {
                            optionalFieldset: {
                                foo: "",
                                bar: "",
                            }
                        };
                        const { formValue, formMeta, updateForm } = useSpsForm(initValue);

                        function handleToggle(isChecked) {
                            console.log(isChecked ? "optional fieldset visible" : "optional fieldset not visible");
                        }

                        return <>
                            <SpsFieldset legend="Fieldset Legend" formMeta={formMeta.fields.optionalFieldset} optional enabled={false} onToggled={handleToggle}>
                                <div className="sfg-row">
                                    <div className="sfg-col-4">
                                        <SpsLabel for={formMeta.fields.optionalFieldset.fields.foo}>Foo</SpsLabel>
                                        <SpsTextInput formMeta={formMeta.fields.optionalFieldset.fields.foo}
                                            value={formValue.optionalFieldset.foo}
                                            placeholder="Enter a thing"
                                        ></SpsTextInput>
                                    </div>
                                    <div className="sfg-col-4">
                                        <SpsLabel for={formMeta.fields.optionalFieldset.fields.bar}>Bar</SpsLabel>
                                        <SpsTextInput formMeta={formMeta.fields.optionalFieldset.fields.bar}
                                            value={formValue.optionalFieldset.bar}
                                            placeholder="Enter a thing"
                                        ></SpsTextInput>
                                    </div>
                                </div>
                            </SpsFieldset>
                        </>
                    }
                `
            }
        }
    },
    d_validationOnField: {
        label: "Validation on Field",
        description: "info about validation on fields",
        examples: {
            validationOnField: {
                react: code`
                    function DemoComponent() {
                        const { formValue, formMeta, updateForm } = useSpsForm({
                            myFieldset: {
                                foo: "",
                            }
                        }, {
                            "myFieldset.foo": [SpsValidators.required]
                        })

                        React.useEffect(() => {
                            formMeta.markAsDirty();
                        }, [])

                        return <>
                            <SpsFieldset formMeta={formMeta.fields.myFieldset} legend="Fieldset Legend">
                                <div className="sfg-row">
                                    <div className="sfg-col-4">
                                        <SpsLabel for={formMeta.fields.myFieldset.fields.foo}>Foo</SpsLabel>
                                        <SpsTextInput formMeta={formMeta.fields.myFieldset.fields.foo}
                                            value={formValue.myFieldset.foo}
                                        ></SpsTextInput>
                                    </div>
                                </div>
                            </SpsFieldset>
                        </>
                    }
                `
            }
        }
    },
    e_topFieldset: {
        label: "Validation on Fieldset",
        description: "info about validation on fieldsets",
        examples: {
            validationOnFieldset: {
                react: code`
                    function DemoComponent() {
                        function mustSelectAColorValidator(value) {
                            if (value.radio === "b") {
                                if (value.colors.filter(Boolean).length === 0) {
                                    return {
                                        mustSelectAColor: true
                                    };
                                }
                            }
                            return null;
                        };

                        const COLOR_OPTIONS = ["Red", "Orange", "Yellow", "Green", "Blue", "Indigo", "Violet"];
                        const { formValue, formMeta, updateForm } = useSpsForm({
                            topFieldset: {
                                text: "",
                                nestedFieldset: {
                                    radio: "b",
                                    colors: COLOR_OPTIONS.map(() => false)
                                }
                            }
                        }, {
                            "topFieldset.nestedFieldset": [mustSelectAColorValidator]
                        });

                        React.useEffect(() => {
                            formMeta.fields.topFieldset.fields.nestedFieldset.fields.radio.markAsDirty();
                        }, [])

                        return (
                            <SpsForm formMeta={formMeta}>
                                <SpsFieldset formMeta={formMeta.fields.topFieldset} legend="Fieldset Legend">
                                    <div className="sfg-row">
                                        <div className="sfg-col-4">
                                            <SpsLabel for={formMeta.fields.topFieldset.fields.text}>Input</SpsLabel>
                                            <SpsTextInput formMeta={formMeta.fields.topFieldset.fields.text}
                                                value={formValue.topFieldset.text}
                                                placeholder="Enter a thing"
                                            ></SpsTextInput>
                                        </div>
                                    </div>
                                    <SpsFieldset formMeta={formMeta.fields.topFieldset.fields.nestedFieldset}
                                        legend="Nested Fieldset"
                                        errors={() => formMeta.fields.topFieldset.fields.nestedFieldset.hasError("mustSelectAColor")
                                            && "If you choose Option B, you must select at least one color."}
                                    >
                                        <div className="sfg-row">
                                            <div className="sfg-col-4">
                                                <SpsLabel for={formMeta.fields.topFieldset.fields.nestedFieldset.fields.radio}>Radio</SpsLabel>
                                                <SpsInputGroup>
                                                    <SpsRadioButton name="radio" label="Option A" value="a"
                                                        formMeta={formMeta.fields.topFieldset.fields.nestedFieldset.fields.radio}
                                                        checked={formValue.topFieldset.nestedFieldset.radio === "a"}
                                                    />
                                                    <SpsRadioButton name="radio" label="Option B" value="b"
                                                        formMeta={formMeta.fields.topFieldset.fields.nestedFieldset.fields.radio}
                                                        checked={formValue.topFieldset.nestedFieldset.radio === "b"}
                                                    />
                                                </SpsInputGroup>
                                            </div>
                                            <div className="sfg-col-8">
                                                <SpsLabel for={formMeta.fields.topFieldset.fields.nestedFieldset.fields.colors}>Colors</SpsLabel>
                                                <SpsInputGroup>
                                                    {COLOR_OPTIONS.map((color, index) =>
                                                        <SpsCheckbox key={index} name="colors" label={color}
                                                            formMeta={formMeta.fields.topFieldset.fields.nestedFieldset.fields.colors[index]}
                                                            checked={formValue.topFieldset.nestedFieldset.colors[index]}
                                                        />
                                                    )}
                                                </SpsInputGroup>
                                            </div>
                                        </div>
                                    </SpsFieldset>
                                </SpsFieldset>
                            </SpsForm>
                        )
                    }
                `
            }
        }
    }
};
